import { Link } from 'react-router-dom';
import { BottomSheet } from './BottomSheet';
import { useProject } from '../../hooks/useProject';
import { formatShort } from '../../lib/date';

export interface AssignmentSheetProps {
  /** The job to show. `null` keeps the sheet closed — mount this unconditionally. */
  jobNumber: string | null;
  onClose: () => void;
}

/** `Jul 30 – Aug 3`, or a single date when the range collapses. Tolerates missing ends. */
function plannedRange(start: string | null, end: string | null): string {
  if (start && end) return start === end ? formatShort(start) : `${formatShort(start)} – ${formatShort(end)}`;
  if (start) return formatShort(start);
  if (end) return formatShort(end);
  return '?';
}

/**
 * The phone counterpart to `ScheduleDetailPanel`: the same project detail, in a bottom sheet
 * instead of a 480px side drawer. Tapping any assignment card opens this.
 *
 * Same read-only content in the same order — context chips, projected window, the read-only
 * notice, the crew table, and a link out to the full project page — so a planner who knows
 * the desktop panel isn't relearning anything on a phone.
 */
export function AssignmentSheet({ jobNumber, onClose }: AssignmentSheetProps) {
  const { data: project, isLoading, error, refetch, isFetching } = useProject(jobNumber);

  const title = project ? `${project.jobNumber} — ${project.name}` : (jobNumber ?? '');

  return (
    <BottomSheet open={!!jobNumber} onClose={onClose} title={title}>
      {isLoading && (
        <div className="space-y-3 animate-pulse" role="status" aria-busy="true">
          <span className="sr-only">Loading project…</span>
          <div className="h-5 w-3/4 bg-slate-100 rounded" />
          <div className="h-4 w-1/2 bg-slate-100 rounded" />
          <div className="h-32 w-full bg-slate-100 rounded" />
        </div>
      )}

      {!!error && !isLoading && (
        <div role="alert" className="text-sm text-brand-charcoal">
          <p>Couldn&apos;t load this project.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="mt-2 min-h-[44px] px-3 -ml-3 text-brand-blue font-medium underline disabled:opacity-50"
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )}

      {project && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {project.serviceLine && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
                {project.serviceLine}
              </span>
            )}
            {project.account && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
                {project.account}
              </span>
            )}
            {project.region && (
              <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
                {project.region}
              </span>
            )}
            <span className="text-xs px-2 py-0.5 rounded-full border border-slate-300 text-brand-charcoal">
              {project.isBillable ? 'Billable' : 'Non-billable'}
            </span>
          </div>

          {(project.projectedStart || project.projectedEnd) && (
            <p className="text-slate-600">
              Projected {plannedRange(project.projectedStart, project.projectedEnd)}
            </p>
          )}

          <p className="text-xs text-slate-500 italic">
            This view is read-only. To update assignments, make changes in the source report and
            wait for the next ingest.
          </p>

          <div>
            <h3 className="text-sm font-semibold text-brand-charcoal mb-2">Crew Assignments</h3>
            {project.assignments.length === 0 ? (
              <p className="text-slate-500">No crew assignments on record for this project.</p>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="py-2 px-3 font-medium">Name</th>
                      <th className="py-2 px-3 font-medium">Planned Dates</th>
                    </tr>
                  </thead>
                  <tbody>
                    {project.assignments.map((a, i) => {
                      // Assignments arrive sorted by person, so a repeat of the previous row's
                      // person is an additional stretch — show the name only once.
                      const isRepeat =
                        i > 0 && project.assignments[i - 1].personId === a.personId;
                      return (
                        <tr
                          key={a.assignmentId}
                          className={isRepeat ? '' : 'border-t border-slate-100'}
                        >
                          <td className="py-2 px-3 font-medium text-brand-charcoal">
                            {isRepeat ? '' : a.personDisplayName}
                          </td>
                          <td className="py-2 px-3 text-slate-600 tabular-nums">
                            {plannedRange(a.plannedStart, a.plannedEnd)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <Link
            to={`/projects/${project.jobNumber}`}
            onClick={onClose}
            className="inline-flex items-center min-h-[44px] text-brand-blue underline"
          >
            View full project →
          </Link>
        </div>
      )}
    </BottomSheet>
  );
}
